import React, { useState, useEffect } from "react";
import * as styles from "./Textfield.module.scss";
import classNames from "classnames";
import { Form } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import PropTypes from "prop-types";
import Tooltip from "@app/components/Tooltip";

const filterAutocompleteOptionsFn = (autocompleteOptions, val) => {
	return autocompleteOptions.filter((option) => option.label?.toLowerCase().indexOf(val?.toLowerCase()) > -1);
};

const Textfield = ({
	type = "text",
	name,
	onBlur,
	onChange,
	disabled = false,
	label,
	id,
	maxLength = 5000,
	rows = 4,
	format,
	placeholder,
	value,
	defaultValue,
	field,
	form,
	tooltip,
	hasAutocomplete = false,
	autocompleteOptions = [],
	autocompleteFooter,
	filterAutocompleteOptions = filterAutocompleteOptionsFn,
	charCountLabelPrefix,
	charCountLabelSufix,
	className,
}) => {
	const [filteredSuggestions, setFilteredSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [charCount, setCharCount] = useState(0);
	const textFieldClassName = classNames({
		[styles["textfield"]]: true,
		[className]: !!className,
	});

	useEffect(() => {
		if (autocompleteOptions && autocompleteOptions.length > 0) {
			setFilteredSuggestions(filterAutocompleteOptions(autocompleteOptions, field ? field.value : defaultValue));
		}
	}, [autocompleteOptions]);

	let extraProps = {};
	if (type === "textArea") {
		extraProps = {
			as: "textarea",
			rows,
		};
	} else {
		extraProps = {
			maxLength,
			type,
		};
	}

	if (hasAutocomplete) {
		extraProps = {
			...extraProps,
			autoComplete: "new-password",
		};
	}

	const fieldName = field ? field.name : name;

	// TODO: Check if we need NumericFormat
	return (
		<Form.Group controlId={fieldName} className={textFieldClassName}>
			{label && !hasAutocomplete && <Form.Label htmlFor={id}>{label}</Form.Label>}
			{label && hasAutocomplete && (
				<label
					htmlFor={fieldName}
					data-text={label}
					className={styles["label-auto-complete"] + " form-label"}
				/>
			)}
			{tooltip && <Tooltip content={<span>{tooltip}</span>} />}
			{format && (
				<div>
					<NumericFormat
						thousandSeparator={format.thousandSeparator}
						decimalSeparator={format.decimalSeparator}
						suffix={format.suffix}
						value={value}
						//defaultValue={''}
						placeholder={format.suffix}
						name={name}
						className={styles["formatted-input"] + " form-control"}
						inputMode="decimal"
						// pattern="[0-9]*"
						maxLength={maxLength}
						// onValueChange={(values, sourceInfo) => {
						// 	console.log("values: ", values)
						// 	console.log("sourceInfo: ", sourceInfo)
						// }}
						onChange={(event) => {
							event.currentTarget.value = event.currentTarget.value
								.split(format.thousandSeparator)
								.join("");
							if (field) {
								field.onChange(event);
							}
						}}
						onBlur={(event) => {
							event.currentTarget.value = event.currentTarget.value
								.split(format.thousandSeparator)
								.join("");
							if (field) {
								field.onBlur(event);
							}
						}}
					/>
				</div>
			)}
			{!format && (
				<div>
					<Form.Control
						{...extraProps}
						autoComplete={"off"} // disabling the browser's AutoComplete
						type={type}
						name={fieldName}
						id={id}
						value={value}
						//defaultValue={''}
						disabled={disabled}
						maxLength={maxLength}
						placeholder={placeholder ? placeholder : ""}
						{...field}
						onFocus={() => {
							if (hasAutocomplete) {
								setShowSuggestions(true);
							}
						}}
						onBlur={(e) => {
							if (field) field.onBlur?.(e);
							if (onBlur && typeof onBlur === "function") onBlur(e);
							if (hasAutocomplete) {
								setShowSuggestions(false);
							}
						}}
						onChange={(e) => {
							if (field) field.onChange?.(e);
							if (onChange && typeof onChange === "function") onChange(e);

							if (hasAutocomplete) {
								setFilteredSuggestions(filterAutocompleteOptions(autocompleteOptions, e.target.value));
								setShowSuggestions(true);
							}

							setCharCount(e.currentTarget.value.length);
						}}
					/>
					{showSuggestions && filteredSuggestions.length > 0 && (
						<div className={styles.suggestions}>
							<ul className={"text-m-relaxed"}>
								{filteredSuggestions.map((suggestion) => (
									<li
										key={`${suggestion.id}-${suggestion.value}`}
										onMouseDown={() => {
											if (form) {
												form.setFieldValue(field.name, suggestion.label, true);
												form.setFieldValue(`${field.name}__ID`, suggestion.value, true);
												form.setFieldValue(`${field.name}__NAME`, suggestion.name, true);
											}
										}}
									>
										{suggestion.label}
									</li>
								))}
							</ul>
							{autocompleteFooter && (
								<div className={styles["suggestions-footer"]}>
									{typeof autocompleteFooter === "function" &&
										autocompleteFooter(filteredSuggestions)}
									{typeof autocompleteFooter !== "function" && autocompleteFooter}
								</div>
							)}
						</div>
					)}
					{type === "textArea" && (
						<div className={styles["textarea-count"]}>
							{charCountLabelPrefix} <span>{maxLength - charCount}</span> {charCountLabelSufix}
						</div>
					)}
				</div>
			)}
		</Form.Group>
	);
};

Textfield.propTypes = {
	name: PropTypes.string.isRequired,
	label: PropTypes.string,
	id: PropTypes.string,
	type: PropTypes.string,
	disabled: PropTypes.bool,
	showGroupError: PropTypes.bool,
	maxLength: PropTypes.number,
	rows: PropTypes.number,
	placeholder: PropTypes.string,
	value: PropTypes.string,
	defaultValue: PropTypes.string,
	className: PropTypes.string,
	onChange: PropTypes.func,
	onBlur: PropTypes.func,
	tooltip: PropTypes.string,
	hasAutocomplete: PropTypes.bool,
	autocompleteOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.any.isRequired,
			label: PropTypes.string.isRequired,
		}).isRequired,
	),
	format: PropTypes.shape({
		thousandSeparator: PropTypes.string,
		decimalSeparator: PropTypes.string,
		suffix: PropTypes.string,
	}),
	charCountLabelPrefix: PropTypes.string,
	charCountLabelSufix: PropTypes.string,
};

export default Textfield;
